import React from 'react';
import styled from 'styled-components';
import {
  IReportState,
  ReportSettings,
} from '../../../data/providers/reports/types';
import { ISettingsTemplate } from '../../../data/providers/settings/reports/types';
import { Entity } from '../../../data/types';
import { OverrideDarkMode } from '../../../Theme';
import { Button } from '@ui/shared/Button';
import { SelectBox } from '../form/SelectBox';
import { LinearLoading } from '../loading/LinearLoading';
import { PdfHeader } from './PdfHeader';

interface IProps {
  onComplete: () => void;
  onStart?: () => void;
  title: string;
  secondaryText?: string;
  children: any;
  filename: string;
  entity: Entity;
  loading?: boolean;
  reportState?: IReportState;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  pdfMode: EPdfMode;
  landscape?: boolean;
}

export enum EPdfMode {
  DEFAULT,
  PREVIEW,
  PRINT,
}

export const Pdf: React.FunctionComponent<IProps> = (
  props: IProps
): React.ReactElement => {
  const handlePrint = (): void => {
    const content = document.getElementById('pdf-export');
    if (!content) return;

    // Open a new window synchronously (must be in click handler to avoid
    // popup blockers). A separate window avoids Safari's PrintCore hang
    // and avoids the "This webpage is trying to print" prompt from iframes.
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Collect all CSS rules from the parent page.
    const cssRules: string[] = [];
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        Array.from(sheet.cssRules).forEach((rule) => {
          cssRules.push(rule.cssText);
        });
      } catch {
        if (sheet.href) {
          cssRules.push(`@import url("${sheet.href}");`);
        }
      }
    });
    const stylesText = cssRules.join('\n');
    const orientation = props.landscape ? 'landscape' : 'portrait';

    // Extract CSS custom properties from the content and its ancestors.
    const cssVarMap = new Map<string, string>();
    let el: HTMLElement | null = content;
    while (el) {
      const computed = getComputedStyle(el);
      for (let i = 0; i < computed.length; i++) {
        const prop = computed[i];
        if (prop.startsWith('--') && !cssVarMap.has(prop)) {
          cssVarMap.set(prop, computed.getPropertyValue(prop));
        }
      }
      el = el.parentElement;
    }
    const cssVars: string[] = [];
    cssVarMap.forEach((value, prop) => {
      cssVars.push(`${prop}: ${value};`);
    });

    const contentBg = getComputedStyle(content).backgroundColor || '#1a1a2e';

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <base href="${window.location.origin}/" />
  <title>${props.filename.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>
  <style>${stylesText}</style>
  <style>
    @page { size: A4 ${orientation}; margin: 0; }
    :root { ${cssVars.join(' ')} }
    html, body {
      margin: 0;
      padding: 0;
      background: ${contentBg};
      width: ${orientation === 'landscape' ? '297mm' : '210mm'} !important;
      max-width: ${orientation === 'landscape' ? '297mm' : '210mm'} !important;
    }
    html, body, body * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    #pdf-export {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow-x: clip !important;
    }
    #pdf-export div {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    * { scrollbar-width: none !important; box-shadow: none !important; }
    *::-webkit-scrollbar { display: none !important; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    [data-html2canvas-ignore="true"] { display: none !important; }
  </style>
</head>
<body>${content.outerHTML}</body>
</html>`);
    printWindow.document.close();

    // Canvas elements lose their drawn content when copied via outerHTML.
    const sourceCanvases = content.querySelectorAll('canvas');
    const printCanvases = printWindow.document.querySelectorAll('canvas');
    sourceCanvases.forEach((srcCanvas: HTMLCanvasElement, i: number) => {
      const printCanvas = printCanvases[i];
      if (!printCanvas) return;
      try {
        const img = printWindow.document.createElement('img');
        img.src = srcCanvas.toDataURL();
        img.width = srcCanvas.width;
        img.height = srcCanvas.height;
        img.style.cssText = printCanvas.style.cssText;
        printCanvas.parentNode?.replaceChild(img, printCanvas);
      } catch {
        // Canvas may be tainted (cross-origin) — leave as-is
      }
    });

    // Make all SVGs inside AutoSizer measuring divs (width: 0) responsive.
    // This handles both Recharts charts and custom SVGs (e.g. asymmetry bars).
    printWindow.document.querySelectorAll('svg').forEach((svg: SVGSVGElement) => {
      const w = svg.getAttribute('width');
      const h = svg.getAttribute('height');
      if (!w || !h || isNaN(Number(w))) return;
      // Walk up to find an AutoSizer measuring div (width: 0px)
      let autoSizerDiv: HTMLElement | null = null;
      let parent = svg.parentElement;
      while (parent && parent !== printWindow.document.body) {
        if (parent.style.width === '0px') {
          autoSizerDiv = parent;
          break;
        }
        parent = parent.parentElement;
      }
      if (!autoSizerDiv) return;
      // Make the SVG responsive
      if (!svg.getAttribute('viewBox')) {
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      }
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', h);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.style.setProperty('width', '100%', 'important');
      // Fix AutoSizer measuring div
      autoSizerDiv.style.setProperty('width', '100%', 'important');
      autoSizerDiv.style.setProperty('overflow', 'visible', 'important');
    });

    // Fix Recharts-specific elements: wrapper width and legend positioning.
    printWindow.document.querySelectorAll('.recharts-wrapper').forEach((wrapper: HTMLElement) => {
      wrapper.style.setProperty('width', '100%', 'important');
      const legend = wrapper.querySelector('.recharts-legend-wrapper') as HTMLElement | null;
      if (legend) {
        legend.style.setProperty('width', '100%', 'important');
      }
    });

    // Fix flex-column card wrappers that contain charts so they don't
    // get compressed. Only target cards with graphs, not table rows.
    printWindow.document.querySelectorAll('.avoid-break').forEach((el: HTMLElement) => {
      if (el.querySelector('.graph-parent') || el.querySelector('.recharts-wrapper')) {
        el.style.setProperty('flex', 'none', 'important');
      }
    });

    // Fix overflow clipping on major containers (Card, ScrollableArea, etc.)
    printWindow.document.querySelectorAll('#pdf-export div').forEach((div: HTMLElement) => {
      const cs = printWindow.getComputedStyle(div);
      if (cs.overflow !== 'visible' || cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
        if (div.classList.contains('recharts-wrapper') ||
            div.classList.contains('recharts-responsive-container') ||
            div.querySelector('.recharts-wrapper')) return;
        div.style.setProperty('overflow', 'visible', 'important');
        if (div.scrollHeight > div.clientHeight * 1.5 && div.scrollHeight - div.clientHeight > 50) {
          if (!div.querySelector('.graph-parent')) {
            const h = div.scrollHeight + 'px';
            div.style.setProperty('height', h, 'important');
            div.style.setProperty('min-height', h, 'important');
          }
        }
      }
    });

    // Propagate backgrounds to row wrappers
    const isTransparent = (c: string): boolean =>
      !c || c === 'rgba(0, 0, 0, 0)' || c === 'transparent';
    printWindow.document.querySelectorAll('.avoid-break').forEach((row: HTMLElement) => {
      if (row.querySelector('svg')) return;
      const bg = printWindow.getComputedStyle(row).backgroundColor;
      if (isTransparent(bg)) {
        let ancestor = row.parentElement;
        while (ancestor) {
          const aBg = printWindow.getComputedStyle(ancestor).backgroundColor;
          if (!isTransparent(aBg)) {
            row.style.backgroundColor = aBg;
            break;
          }
          ancestor = ancestor.parentElement;
        }
      }
    });

    // Fix EntityCell borders
    const separatorColor = getComputedStyle(content).getPropertyValue('--color-separator').trim();
    printWindow.document.querySelectorAll('[data-entity-cell]').forEach((cell: HTMLElement) => {
      cell.style.borderTop = `1px solid ${separatorColor}`;
      cell.style.borderRight = `1px solid ${separatorColor}`;
      cell.style.borderBottom = `1px solid ${separatorColor}`;
      cell.style.borderLeft = 'none';
    });

    // Explicitly load fonts in the new window, then print.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pw = printWindow as any;
    const iconFont = new pw.FontFace(
      'Material Symbols Outlined',
      `url(${window.location.origin}/material-symbols-outlined.woff2)`,
      { style: 'normal', weight: '100 700' }
    );
    const robotoFont = new pw.FontFace(
      'Roboto',
      `url(${window.location.origin}/roboto-v29-latin-regular.woff2)`,
      { style: 'normal', weight: '400' }
    );
    const robotoBoldFont = new pw.FontFace(
      'Roboto',
      `url(${window.location.origin}/roboto-v29-latin-700.woff2)`,
      { style: 'normal', weight: '700' }
    );

    printWindow.document.fonts.add(iconFont);
    printWindow.document.fonts.add(robotoFont);
    printWindow.document.fonts.add(robotoBoldFont);

    Promise.all([iconFont.load(), robotoFont.load(), robotoBoldFont.load()])
      .catch(() => { /* fonts failed to load – print anyway */ })
      .then(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      });
  };

  const printModeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    props.onPdfModeChange(
      e.currentTarget.checked ? EPdfMode.PRINT : EPdfMode.PREVIEW
    );
  };

  return (
    <React.Fragment>
      <div style={{ height: '100%', width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50px',
            backgroundColor: 'var(--color-surface-1)',
          }}
        >
          <PdfActions>
            <div style={{ flex: 1 }} />
            <PdfActionCenter>
              <Button
                icon='print'
                title='Print / Save as PDF'
                onClick={handlePrint}
                disabled={props.loading || !props.reportState?.isValid}
              />
            </PdfActionCenter>
            <PdfActionRight>
              <SelectBox
                label='Printable View'
                selected={props.pdfMode === EPdfMode.PRINT}
                onSelect={printModeHandler}
              />
            </PdfActionRight>
          </PdfActions>
        </div>
        {props.loading ? (
          <LinearLoading />
        ) : (
          <PdfPreviewContainer>
            <PdfPageWrapper>
              <OverrideDarkMode
                override={props.pdfMode === EPdfMode.PRINT}
                id='pdf-export'
                style={{
                  backgroundColor: 'var(--color-surface-0)',
                  border: '2px solid var(--color-surface-0)',
                }}
              >
                <PdfHeader
                  title={props.title}
                  entity={props.entity}
                  timeframe={props.reportState?.timeframe}
                  templateName={
                    (
                      props.reportState
                        ?.settings as ISettingsTemplate<ReportSettings>
                    )?.name
                  }
                />
                {props.children}
              </OverrideDarkMode>
            </PdfPageWrapper>
          </PdfPreviewContainer>
        )}
      </div>
    </React.Fragment>
  );
};

const PdfActions = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const PdfActionCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const PdfActionRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const PdfPreviewContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background: rgba(0, 0, 0, 1);
`;

// A4 full width (210mm) at 96 CSS px/in ≈ 794px.
const A4_WIDTH = 794;

const PdfPageWrapper = styled.div`
  width: ${A4_WIDTH}px;
  margin: 20px auto;
`;

export const isPdf = (mode: EPdfMode): boolean => {
  return mode === EPdfMode.PREVIEW || mode === EPdfMode.PRINT;
};

export const shouldInvert = (mode: EPdfMode): boolean =>
  mode === EPdfMode.PRINT;
